import { CurrentUser } from 'customTypes'
import { getVideoCallsFlowCustomizationParams } from 'hooks/useVideoCallsFlowCustomization'
import { LocationPaths, PaidVideoCallsPath } from 'location.types'
import { PARTNER_ID_KEY } from 'screens/VideoCalls/utils/constants'
import {
  getDexcomConnection,
  isDexcomVideoCallPartner
} from 'screens/VideoCalls/utils/dexcomPartner'
import { resolveReferralPartner } from 'screens/VideoCalls/utils/referralPartner'
import { isKeggWebviewFlow } from 'utils/keggWebviewFlow'
import { LazyComponentWithPreload, lazyPreload } from 'utils/lazyPreload'
import { safeGetLocalStorageItem } from 'utils/localStorage'
import { getUserData } from 'utils/userData'
import { EhrAppointment } from 'types'

const PaidCallIntroScreen = lazyPreload(() =>
  import('./PaidCallIntroScreen').then((module) => ({
    default: module.PaidCallIntroScreen
  }))
)

const PaidCallSignUpScreen = lazyPreload(() =>
  import('./PaidCallSignUpScreen').then((module) => ({
    default: module.PaidCallSignUpScreen
  }))
)

const PaidCallSubmitStateScreen = lazyPreload(() =>
  import('./PaidCallSubmitStateScreen').then((module) => ({
    default: module.PaidCallSubmitStateScreen
  }))
)

const ScheduleCallScreen = lazyPreload(() =>
  import('screens/VideoCalls').then((module) => ({
    default: module.ScheduleCallStep
  }))
)

const PaidCallCheckoutScreen = lazyPreload(() =>
  import('./PaidCallCheckoutScreen').then((module) => ({
    default: module.PaidCallCheckoutScreen
  }))
)

const PaidCallCompletedScreen = lazyPreload(() =>
  import('./PaidCallCompletedScreen').then((module) => ({
    default: module.PaidCallCompletedScreen
  }))
)

const KeggInsuranceNotSupportedScreen = lazyPreload(() =>
  import('screens/VideoCalls/Kegg/InsuranceNotSupported').then((module) => ({
    default: module.KeggInsuranceNotSupported
  }))
)

const KeggPaidCallConfirmedScreen = lazyPreload(() =>
  import('screens/VideoCalls/Kegg/PaidCallConfirmed').then((module) => ({
    default: module.KeggPaidCallConfirmed
  }))
)

const AppointmentConfirmedScreen = lazyPreload(() =>
  import('./AppointmentConfirmedScreen').then((module) => ({
    default: module.AppointmentConfirmedScreen
  }))
)

const IntegrateDexcomAccountScreen = lazyPreload(() =>
  import('screens/VideoCalls/IntegrateDexcomAccount').then((module) => ({
    default: module.IntegrateDexcomAccount
  }))
)

export interface PaidVideoCallStep {
  path: PaidVideoCallsPath | LocationPaths.PaidVideoCalls
  location: string
  completedResolver: (
    user: CurrentUser | undefined,
    latestAppointment: EhrAppointment | undefined
  ) => boolean
  canNavigateBack: boolean
  routeComponent: LazyComponentWithPreload<() => JSX.Element | null>
  requiresStripe?: boolean
  hideSecurityNote?: boolean
  hideProgressBar?: boolean
}

export const getPaidVideoCallFlow: () => PaidVideoCallStep[] = () => {
  const partner = safeGetLocalStorageItem(PARTNER_ID_KEY)
  const { forceKeggFlow } = getVideoCallsFlowCustomizationParams()

  const isDexcomFlow = isDexcomVideoCallPartner(partner)

  const signUpStep: PaidVideoCallStep = {
    path: PaidVideoCallsPath.UserInfo,
    location: `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.UserInfo}`,
    completedResolver: (user) => !!user?.email,
    canNavigateBack: true,
    routeComponent: PaidCallSignUpScreen
  }

  const stateStep: PaidVideoCallStep = {
    path: PaidVideoCallsPath.State,
    location: `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.State}`,
    completedResolver: (user) => {
      const userData = getUserData()
      return !!user?.address?.state || !!userData?.state
    },
    canNavigateBack: true,
    routeComponent: PaidCallSubmitStateScreen
  }

  const integrateDexcomAccountStep: PaidVideoCallStep = {
    path: PaidVideoCallsPath.IntegrateDexcomAccount,
    location: `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.IntegrateDexcomAccount}`,
    completedResolver: (user) => {
      const resolvedPartner = resolveReferralPartner(user?.referralPartner.key, partner)
      return !!getDexcomConnection(user, resolvedPartner)
    },
    canNavigateBack: true,
    routeComponent: IntegrateDexcomAccountScreen
  }

  // This one is exclusive to Dexcom Users
  // since we need to control via feature flags the upsell steps they will see
  // Its easier to have a separated screen for it
  const appointmentConfirmedStep: PaidVideoCallStep = {
    path: PaidVideoCallsPath.AppointmentConfirmed,
    location: `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.AppointmentConfirmed}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: false,
    routeComponent: AppointmentConfirmedScreen
  }

  const completedStep: PaidVideoCallStep = {
    path: PaidVideoCallsPath.Completed,
    location: `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.Completed}`,
    completedResolver: () => false,
    canNavigateBack: false,
    routeComponent: isKeggWebviewFlow(partner, forceKeggFlow)
      ? KeggPaidCallConfirmedScreen
      : PaidCallCompletedScreen,
    hideProgressBar: isKeggWebviewFlow(partner, forceKeggFlow),
    hideSecurityNote: true
  }

  const result: PaidVideoCallStep[] = [
    {
      path: LocationPaths.PaidVideoCalls,
      location: `${LocationPaths.PaidVideoCalls}`,
      completedResolver: () => false,
      canNavigateBack: true,
      routeComponent: isKeggWebviewFlow(partner, forceKeggFlow)
        ? KeggInsuranceNotSupportedScreen
        : PaidCallIntroScreen
    },
    signUpStep,
    stateStep,
    {
      path: PaidVideoCallsPath.ScheduleCall,
      location: `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.ScheduleCall}`,
      completedResolver: () => false,
      canNavigateBack: true,
      routeComponent: ScheduleCallScreen
    },
    {
      path: PaidVideoCallsPath.Checkout,
      location: `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.Checkout}`,
      completedResolver: () => false,
      canNavigateBack: isKeggWebviewFlow(partner, forceKeggFlow),
      requiresStripe: true,
      routeComponent: PaidCallCheckoutScreen,
      hideSecurityNote: true
    }
  ]

  if (isDexcomFlow) {
    result.push(appointmentConfirmedStep, integrateDexcomAccountStep)
  }

  result.push(completedStep)

  return result
}
